import React, { useCallback, useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { adminApi } from "../../api";
import Logo from "../../components/Logo";
import {
  LayoutDashboard, Package, Newspaper, FileEdit, FileQuestion, Mail, Settings, LogOut, ExternalLink, Menu, X,
} from "lucide-react";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/articles", label: "Blog", icon: Newspaper },
  { to: "/admin/content", label: "Site Content", icon: FileEdit },
  { to: "/admin/rfq", label: "RFQ Enquiries", icon: FileQuestion, badge: "rfqs_unread" },
  { to: "/admin/messages", label: "Messages", icon: Mail, badge: "messages_unread" },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, checking, logout } = useAdminAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false); // mobile drawer
  const [counts, setCounts] = useState({});

  const loadCounts = useCallback(() => {
    adminApi.get("/admin/stats").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  // Refresh unread badges on navigation and whenever the inbox marks/deletes an item.
  useEffect(() => { if (user) loadCounts(); }, [user, pathname, loadCounts]);
  useEffect(() => {
    window.addEventListener("admin-counts-changed", loadCounts);
    return () => window.removeEventListener("admin-counts-changed", loadCounts);
  }, [loadCounts]);
  // Close the drawer after navigating
  useEffect(() => { setOpen(false); }, [pathname]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0c1626] text-white/70">
        Loading admin console...
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#f1f7fb] lg:flex">
      {/* Mobile backdrop */}
      {open && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} aria-hidden="true" />}

      {/* Sidebar (off-canvas on small screens) */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#0c1626] text-white/80 flex flex-col transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:shrink-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 flex items-center gap-3 border-b border-white/10">
          <span className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
            <Logo size={28} />
          </span>
          <span className="leading-none flex-1">
            <span className="block font-display text-lg text-white">Novocreste</span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-[#7fc6d6]">Admin</span>
          </span>
          <button onClick={() => setOpen(false)} aria-label="Close menu" className="lg:hidden h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {links.map((l) => {
            const n = l.badge ? counts[l.badge] : 0;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-[#0e7490] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <l.icon style={{ width: 18, height: 18 }} />
                <span className="flex-1">{l.label}</span>
                {n > 0 && (
                  <span className="min-w-5 h-5 px-1.5 rounded-full bg-[#d9a441] text-[#0c1626] text-[11px] font-semibold flex items-center justify-center" aria-label={`${n} unread`}>
                    {n}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-1">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors">
            <ExternalLink style={{ width: 18, height: 18 }} /> View website
          </Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors">
            <LogOut style={{ width: 18, height: 18 }} /> Logout
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-border flex items-center gap-3 px-4 sm:px-8 sticky top-0 z-20">
          <button onClick={() => setOpen(true)} aria-label="Open menu" className="lg:hidden h-10 w-10 -ml-1 rounded-lg border border-border flex items-center justify-center text-[#0f1e33]">
            <Menu className="h-5 w-5" />
          </button>
          <p className="text-sm text-[#5a6b7e]">Signed in as <span className="font-medium text-[#0f1e33]">{user.username}</span></p>
        </header>
        <div className="p-4 sm:p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
